import { PAGINATION } from '@/constants/pagination.constant';
import { MessageType } from '@/types/common';
import { createAppSlice } from '../createAppSlice';
import { transformerArray, transformerObject } from '../transformer';
import { Message } from '@/enums/message.enum';
import { MESSAGE } from '@/constants/message.constant';
import { PayloadAction } from '@reduxjs/toolkit';

type BannerSliceState = {
  loading: boolean;
  listBanner: Banner[];
  listBannerById: Banner[];
  message?: MessageType;
  pagination: Pagination;
  bannerDetail?: BannerDetail;
  timeLineBanner?: number;
  showPopup?: boolean;
  sortBannerLoading?: boolean;
};

const initialState: BannerSliceState = {
  loading: false,
  listBanner: [],
  pagination: PAGINATION,
  listBannerById: [],
  showPopup: false,
  sortBannerLoading: false
};

export const bannerSlice = createAppSlice({
  name: 'banner',
  initialState,
  reducers: (create) => ({
    getListBanner: create.asyncThunk(
      async (query?: Record<string, any>) => {
        const response = await apiFetcher.get('banner', { query });
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listBanner = transformerArray(action.payload.listData);
          state.pagination = action.payload.paging;
          state.timeLineBanner = action.payload.listData[0]?.NUMBER_MOVEMENT;
        },
        rejected: (state) => {
          state.loading = false;
          state.listBanner = [];
          state.pagination = PAGINATION;
        }
      }
    ),
    getListBannerById: create.asyncThunk(
      async (query?: Record<string, any>) => {
        const response = await apiFetcher.get('banner', { query });
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listBannerById = transformerArray(action.payload.listData);
        },
        rejected: (state) => {
          state.loading = false;
          state.listBannerById = [];
        }
      }
    ),
    deleteBanner: create.asyncThunk(
      async (id: number) => {
        await apiFetcher.post(`banner/delete`, { id });
        return id;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listBanner = state.listBanner.filter(
            (banner) => banner.id !== String(action.payload)
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_BANNER_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_BANNER_FAIL
          };
        }
      }
    ),
    createBannerSlider: create.asyncThunk(
      async (body: any) => {
        await apiFetcher.post('banner/create', body);
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_BANNER_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.showPopup = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.CREATE_BANNER_FAIL
          };
        }
      }
    ),
    updateBannerSlider: create.asyncThunk(
      async (body: any) => {
        await apiFetcher.post(`banner/update`, body);
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_BANNER_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_BANNER_FAIL
          };
        }
      }
    ),
    getBannerById: create.asyncThunk(
      async (id: string) => {
        const response = await apiFetcher.post(`banner/detail`, { id });
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.bannerDetail = transformerObject(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.bannerDetail = undefined;
        }
      }
    ),
    createTimeBannerSlider: create.asyncThunk(
      async (data: BannerTime) => {
        await apiFetcher.post('banner/setting-time-movement-banner', data);
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_BANNER_TIME_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_BANNER_TIME_FAIL
          };
        }
      }
    ),
    sortBannerSlider: create.asyncThunk(
      async (data: SortBanner[]) => {
        await apiFetcher.post('banner/list-banner-sort-order', {
          listSortOrder: JSON.stringify(data)
        });
      },
      {
        pending: (state) => {
          state.sortBannerLoading = true;
        },
        fulfilled: (state) => {
          state.sortBannerLoading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.SORT_BANNER_TIME_SUCCESS
          };
        },
        rejected: (state) => {
          state.sortBannerLoading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.SORT_BANNER_TIME_FAIL
          };
        }
      }
    ),

    resetMessages: create.reducer((state) => {
      state.message = undefined;
    }),

    showPopup: create.reducer((state, action: PayloadAction<boolean>) => {
      state.showPopup = action.payload;
    }),
    setBannerTimer: create.reducer((state, action: PayloadAction<number>) => {
      state.timeLineBanner = action.payload;
    })
  }),
  selectors: {
    watchListBanner: (banner) => banner.listBanner,
    watchBannerDetail: (banner) => banner.bannerDetail,
    watchListBannerById: (bannerById) => bannerById.listBannerById,
    watchBannerMessage: (banner) => banner.message,
    watchBannerLoading: (banner) => banner.sortBannerLoading,
    watchBannerPagination: (banner) => banner.pagination,
    watchTimeBanner: (banner) => banner.timeLineBanner
  }
});

export const {
  watchListBanner,
  watchBannerMessage,
  watchListBannerById,
  watchBannerLoading,
  watchBannerPagination,
  watchBannerDetail,
  watchTimeBanner
} = bannerSlice.selectors;

export const {
  getListBanner,
  resetMessages,
  showPopup,
  deleteBanner,
  getListBannerById,
  createBannerSlider,
  getBannerById,
  updateBannerSlider,
  createTimeBannerSlider,
  sortBannerSlider,
  setBannerTimer
} = bannerSlice.actions;
